import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { ReactiveFormsModule, FormArray, FormGroup } from '@angular/forms';

// Kendo UI imports
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule, CheckBoxModule } from '@progress/kendo-angular-inputs';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  bugOutlineIcon,
  checkIcon,
  envelopIcon,
  laptopOutlineIcon,
  securityCheckOutlineIcon,
  SVGIcon,
  userIcon,
  truckIcon,
  gearIcon,
  infoCircleIcon,
  plusIcon,
  minusIcon,
  exclamationCircleIcon,
} from '@progress/kendo-svg-icons';

// Core imports
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { CustomerInfoData } from 'src/app/core/models/customer';
import { CustomerInfoFormService } from './services/form.service';

@Component({
  selector: 'app-customer-info',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IconsModule,
    InputsModule,
    CheckBoxModule,
    DateInputsModule,
    LabelModule,
    LayoutModule,
    DropDownsModule,
    ButtonsModule,
  ],
  providers: [CustomerInfoFormService],
  templateUrl: './customer-info.component.html',
  styleUrls: ['./customer-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerInfoComponent extends BaseComponent {
  private formService = inject(CustomerInfoFormService);

  // Input/Output
  public formData = input<CustomerInfoData>();
  public dataUpdate = output<CustomerInfoData>();

  // Icons
  public securityCheckOutlineIcon: SVGIcon = securityCheckOutlineIcon;
  public laptopOutlineIcon: SVGIcon = laptopOutlineIcon;
  public bugOutlineIcon: SVGIcon = bugOutlineIcon;
  public envelopIcon: SVGIcon = envelopIcon;
  public userIcon: SVGIcon = userIcon;
  public checkIcon: SVGIcon = checkIcon;
  public truckIcon: SVGIcon = truckIcon;
  public geoAltIcon: SVGIcon = gearIcon; // Using gearIcon as closest alternative
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public plusIcon: SVGIcon = plusIcon;
  public minusIcon: SVGIcon = minusIcon;
  public exclamationTriangleIcon: SVGIcon = exclamationCircleIcon;

  // Form
  public customerInfoForm = this.formService.createCustomerInfoForm();

  // Signal to track selected services
  public selectedServicesSignal = signal<string[]>([]);

  // Computed properties
  public isFormValid = computed(() => this.customerInfoForm.valid);
  public selectedServices = computed(() => this.selectedServicesSignal());
  public additionalLocationsArray = computed(() => {
    return this.customerInfoForm.get('additionalLocations') as FormArray;
  });

  // Service data
  public availableServices = this.formService.getAvailableServices().map((service) => ({
    ...service,
    icon: this.getIconByName(service.icon),
  }));
  public slaOptions = this.formService.getSlaOptions();

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Watch for form changes and emit updates
    this.customerInfoForm.valueChanges.subscribe((value) => {
      const cleanValue = this.formService.getFormData(this.customerInfoForm);
      this.dataUpdate.emit(cleanValue);
    });

    // Initialize with existing data if available
    const existingData = this.formData();
    if (existingData) {
      // Recreate form with existing data
      this.customerInfoForm = this.formService.createCustomerInfoForm(existingData);

      // Sync the signal with existing data
      this.selectedServicesSignal.set(existingData.selectedServices);
    }
  }

  private getIconByName(iconName: string): SVGIcon {
    const iconMap: { [key: string]: SVGIcon } = {
      securityCheckOutlineIcon: this.securityCheckOutlineIcon,
      laptopOutlineIcon: this.laptopOutlineIcon,
      bugOutlineIcon: this.bugOutlineIcon,
      envelopIcon: this.envelopIcon,
      userIcon: this.userIcon,
    };
    return iconMap[iconName] || this.userIcon;
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.selectedServicesSignal().includes(serviceValue);
  }

  public toggleService(serviceValue: string): void {
    const currentValue = this.selectedServicesSignal();
    const selectedServices = [...currentValue];
    const index = selectedServices.indexOf(serviceValue);

    if (index > -1) {
      selectedServices.splice(index, 1);
    } else {
      selectedServices.push(serviceValue);
    }

    // Update both the signal and the form control
    this.selectedServicesSignal.set(selectedServices);

    const selectedServicesControl = this.customerInfoForm.get('selectedServices');
    if (selectedServicesControl) {
      selectedServicesControl.setValue(selectedServices, { emitEvent: true });
      selectedServicesControl.markAsTouched();
    }
  }

  public addAdditionalLocation(): void {
    this.formService.addAdditionalLocation(this.customerInfoForm);
  }

  public removeAdditionalLocation(index: number): void {
    this.formService.removeAdditionalLocation(this.customerInfoForm, index);
  }

  public getAdditionalLocationFormGroup(index: number): FormGroup {
    const additionalLocations = this.customerInfoForm.get('additionalLocations') as FormArray;
    return additionalLocations.at(index) as FormGroup;
  }
}
